import { uiText } from "@/lib/i18n/uiText";
import { useEffect, useState } from "react";
import { Globe, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/components/i18n/LanguageProvider";
import { DEFAULT_LOCALE, getLocale, isEnabledLocale, matchBrowserLocale } from "@/lib/i18n/locales";

const STORAGE_KEY = "statlign_locale";
const DISMISS_KEY = "statlign_locale_suggestion_dismissed";

/** Crawlers must never be redirected or nudged — they see the page as served. */
const BOT_RE =
  /(bot|crawl|spider|slurp|adsbot|mediapartners|bingpreview|duckduckbot|yandex|baiduspider|applebot|facebookexternalhit|lighthouse|headlesschrome)/i;

function isCrawler(): boolean {
  if (typeof navigator === "undefined") return false;
  return BOT_RE.test(navigator.userAgent || "");
}

function browserCandidates(): string[] {
  if (typeof navigator === "undefined") return [];
  const langs = Array.isArray(navigator.languages) && navigator.languages.length
    ? [...navigator.languages]
    : [navigator.language].filter(Boolean);
  return langs as string[];
}

/**
 * First-visit language suggestion.
 *
 * When a visitor has no stored preference and their browser languages point at a
 * locale we serve, we show a dismissible banner offering that version — we never
 * force a redirect, an explicit URL prefix always wins, and crawlers are skipped
 * entirely so indexing stays deterministic.
 */
export function LanguageBanner() {
  const ctx = useLanguage();
  const locale = ctx?.locale ?? DEFAULT_LOCALE;
  const [suggestion, setSuggestion] = useState<string | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (isCrawler()) return;

    let stored: string | null = null;
    let dismissed = false;
    try {
      stored = localStorage.getItem(STORAGE_KEY);
      dismissed = localStorage.getItem(DISMISS_KEY) === "1";
    } catch {
      return;
    }
    // An explicit prefix in the URL, a stored choice or a prior dismissal all win.
    if (dismissed) return;
    if (stored && isEnabledLocale(stored)) return;
    if (locale !== DEFAULT_LOCALE) return;

    const best = matchBrowserLocale(browserCandidates());
    if (!best || best === locale) return;
    setSuggestion(best);
  }, [locale]);

  if (!suggestion) return null;
  const def = getLocale(suggestion);

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, "1");
    } catch {
      /* ignore private-mode storage errors */
    }
    setSuggestion(null);
  };

  return (
    <div
      role="region"
      aria-label={uiText("Language suggestion")}
      className="border-b bg-muted/60 px-4 py-2 text-sm"
    >
      <div className="mx-auto flex max-w-5xl flex-wrap items-center gap-2">
        <Globe className="h-4 w-4 text-primary" aria-hidden="true" />
        <span className="flex-1">
           {uiText("Statlign is available in")} <strong>{def.nativeName}</strong>{uiText(". Switch to the localized version?")} </span>
        <Button
          size="sm"
          onClick={() => {
            try {
              ctx?.setLocale(suggestion);
            } finally {
              dismiss();
            }
          }}
          aria-label={uiText("Switch site language to {{v0}}", { v0: def.englishName })}
        >
          {def.nativeName}
        </Button>
        <Button size="sm" variant="ghost" onClick={dismiss} aria-label={uiText("Keep the current language")}>
           {uiText("Stay in English")} </Button>
        <Button
          size="icon"
          variant="ghost"
          className="h-7 w-7"
          onClick={dismiss}
          aria-label={uiText("Dismiss language suggestion")}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
